import { useEffect, useRef, useCallback } from 'react';
import { useChatContext } from '../contexts/ChatContext';
import { getAuthToken, isTokenExpired, decodeToken, aggressiveLogout } from '../utils/authHelpers';

/**
 * AuthSessionManager - Monitors JWT token expiry and triggers logout when needed.
 * 
 * This component:
 * - Checks token validity on mount, focus, and visibility change
 * - Schedules automatic logout when token is about to expire
 * - Immediately logs out if token is already expired
 * 
 * Must be rendered inside ChatProvider to access disconnectChat.
 */
const AuthSessionManager = () => {
  const { disconnectChat } = useChatContext();
  const expiryTimerRef = useRef(null);

  /**
   * Trigger session expiry logout
   */
  const handleSessionExpired = useCallback(() => {
    console.warn('🔐 Session expired - logging out');
    aggressiveLogout(disconnectChat, { reason: 'session_expired' });
  }, [disconnectChat]);

  /**
   * Check token validity and schedule expiry if valid
   */
  const checkAndScheduleExpiry = useCallback(() => {
    const token = getAuthToken();
    
    // No token = not logged in, nothing to do
    if (!token) {
      if (expiryTimerRef.current) {
        clearTimeout(expiryTimerRef.current);
        expiryTimerRef.current = null;
      }
      return;
    }

    // Token expired - logout immediately
    if (isTokenExpired(token)) {
      handleSessionExpired();
      return;
    }

    // Token valid - schedule logout for when it expires
    const decoded = decodeToken(token);
    if (decoded && decoded.exp) {
      // Clear any existing timer
      if (expiryTimerRef.current) {
        clearTimeout(expiryTimerRef.current);
      }

      const expiresAt = decoded.exp * 1000; // Convert to milliseconds
      const now = Date.now();
      const msUntilExpiry = expiresAt - now;

      // Add a small buffer (5 seconds before actual expiry) to avoid race conditions
      const msUntilLogout = Math.max(0, msUntilExpiry - 5000);

      // Only schedule if expiry is within a reasonable timeframe (< 8 days)
      // This prevents issues with very long-lived tokens or invalid exp values
      const maxScheduleMs = 8 * 24 * 60 * 60 * 1000; // 8 days
      if (msUntilLogout > 0 && msUntilLogout < maxScheduleMs) {
        console.log(`🔐 Token expires in ${Math.round(msUntilLogout / 1000 / 60)} minutes - scheduled logout`);
        expiryTimerRef.current = setTimeout(() => {
          handleSessionExpired();
        }, msUntilLogout);
      }
    }
  }, [handleSessionExpired]);

  // Check on mount
  useEffect(() => {
    checkAndScheduleExpiry();

    // Cleanup timer on unmount
    return () => {
      if (expiryTimerRef.current) {
        clearTimeout(expiryTimerRef.current);
      }
    };
  }, [checkAndScheduleExpiry]);

  // Check on window focus and visibility change
  useEffect(() => {
    const handleFocus = () => {
      checkAndScheduleExpiry();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkAndScheduleExpiry();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkAndScheduleExpiry]);

  // This component doesn't render anything
  return null;
};

export default AuthSessionManager;
